import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnInit,
  ViewChild,
} from '@angular/core';

import { FormlyFieldConfig } from '@ngx-formly/core';
import { StoreService } from '../../../store.service';

import {
  Companies,
  CompanyAddress_Insert_Input,
  currencyOptions,
  Result,
} from '@etoh/database/core';
import { DatasService } from 'apps/desktop/src/app/datas.service';
import { getDiffArrayOperation } from 'apps/desktop/src/app/dynamic-form/utilities';
import { UserService } from 'apps/desktop/src/app/user.service';
import { omit } from 'lodash';
import { combineLatest, map, of, switchMap, take, tap } from 'rxjs';
import { AddressesService } from '../../../addresses/addresses.service';
import { AddressesAddComponent } from '../../../addresses/containers/addresses-add/addresses-add.component';
import { TransactionsService } from '../../../agreements/transactions.service';
import { EntitiesService } from '../../../entities/entities.service';
import { OfficesService } from '../../../offices/offices.service';
import { SamplesService } from '../../../samples/samples.service';
import { PageFormComponent } from '../../../shared/components/layout/page-form/page-form.component';
import { getCountryOptions } from '../../../shared/forms/countries.datas';
import { FormsService } from '../../../shared/forms/forms.service';
import { CommentsNotesFormAndAttachedFilesForm } from '../../../shared/forms/templates.forms';
import {
  PageFormInterface,
  PageFormMode,
} from '../../../shared/interfaces/pageForm.interface';
import { TabsTable } from '../../../shared/interfaces/tabsTable.interface';
import { UsersService } from '../../../users.service';

@Component({
  selector: 'etoh-company-add',
  templateUrl: './company-add.component.html',
  styleUrls: ['./company-add.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [PageFormComponent],
})
export class CompanyAddComponent implements OnInit, PageFormInterface {
  @ViewChild(PageFormComponent, { static: false })
  pageForm!: PageFormComponent;
  @Input() id: number;
  @Input() standalone = true;

  fields: FormlyFieldConfig[];

  dataTabs: TabsTable[];

  afterGetModel = (model: any) => {
    return this.addressesService.getAddressesIdForCompany$(model.id).pipe(
      map((addressesId) => {
        return {
          ...model,
          addresses: addressesId,
        };
      })
    );
  };

  // Check if name is duplicated
  shouldPrevent = (model: any, mode: PageFormMode) => {
    if (mode === 'edit') {
      return of(false);
    }
    return this.entitiesService.getCorrespondanceAndShowDialog<Companies>(
      'companies',
      ['name'],
      model?.name
    );
  };

  constructor(
    private storeService: StoreService,
    private usersService: UsersService,
    private formsService: FormsService,
    private addressesService: AddressesService,
    private datasService: DatasService,
    private userService: UserService,
    private officesService: OfficesService,
    private entitiesService: EntitiesService,
    private transactionsService: TransactionsService,
    private samplesService: SamplesService
  ) {}

  async ngOnInit() {
    this.fields = [
      {
        fieldGroupClassName: 'ant-row layout-padding',
        fieldGroup: [
          {
            key: 'name',
            type: 'input',
            className: 'ant-col ant-col-third',
            props: {
              type: 'text',
              label: 'Company name',
              required: true,
            },
          },
          {
            key: 'category',
            type: 'input',
            className: 'ant-col ant-col-third',
            props: {
              type: 'text',
              label: 'Category',
              required: false,
              options: this.formsService.getOptionsFromPreviousItems(
                'companies',
                'category'
              ),
            },
          },
          {
            key: 'certifications',
            type: 'tags',
            className: 'ant-col ant-col-third',
            props: {
              label: 'Certifications',
              required: false,
              options: this.formsService.getOptionsFromPreviousItems(
                'companies',
                'certifications'
              ),
            },
          },
          {
            key: 'officialId',
            type: 'input',
            className: 'ant-col ant-col-third',
            props: {
              type: 'text',
              label: 'Official ID Number',
              required: false,
            },
          },
          {
            key: 'vat',
            type: 'input',
            className: 'ant-col ant-col-third',
            props: {
              type: 'text',
              label: 'VAT ID Number',
              required: false,
            },
          },
          {
            key: 'eori',
            type: 'input',
            className: 'ant-col ant-col-third',
            props: {
              type: 'text',
              label: 'EORI',
              required: false,
            },
          },
          {
            key: 'fda',
            type: 'input',
            className: 'ant-col ant-col-third',
            props: {
              type: 'text',
              label: 'FDA',
              required: false,
            },
          },
          {
            key: 'cvi',
            type: 'input',
            className: 'ant-col ant-col-third',
            props: {
              type: 'text',
              label: 'CVI',
              required: false,
            },
          },
          {
            key: 'phone',
            type: 'phone',
            className: 'ant-col ant-col-third',
            props: {
              label: 'Phone number',
              required: false,
            },
          },
          {
            key: 'email',
            type: 'input',
            className: 'ant-col ant-col-third',
            props: {
              type: 'email',
              label: 'Email',
              required: false,
            },
            validators: {
              validation: ['email'],
            },
          },
          {
            key: 'officeId',
            type: 'select',
            className: 'ant-col ant-col-third',
            defaultValue: this.userService.userOfficeId$.value,
            props: {
              label: 'Office',
              required: false,
              options: this.officesService.getOfficesForOptions$(),
            },
          },
          {
            key: 'brokerId',
            type: 'select',
            className: 'ant-col ant-col-third',
            defaultValue: this.userService.userId$.value,
            props: {
              label: 'Broker',
              required: false,
              options: this.usersService.getBrokersForOptions$(),
            },
            hooks: {
              onInit: (field: FormlyFieldConfig) => {
                this.formsService.setOptionsDependantOf(
                  field,
                  ['officeId'],
                  ([officeId]) => {
                    return this.usersService.getBrokersForOptions$(officeId);
                  }
                );
              },
            },
          },
          {
            key: 'country',
            type: 'select',
            className: 'ant-col ant-col-third',
            props: {
              label: 'Country',
              placeholder: '',
              required: false,
              options: getCountryOptions(),
            },
          },
          {
            key: 'scoring',
            type: 'input',
            className: 'ant-col ant-col-third',
            props: {
              type: 'text',
              label: 'Scoring',
              required: false,
            },
          },
          {
            key: 'parentCompany',
            type: 'input',
            className: 'ant-col ant-col-third',
            props: {
              type: 'text',
              label: 'Parent company',
              required: false,
            },
          },
          {
            key: 'targetObjective',
            type: 'input',
            className: 'ant-col ant-col-third',
            props: {
              type: 'number',
              label: 'Target objective',
              required: false,
            },
          },
          {
            key: 'targetObjectiveCurrency',
            type: 'select',
            className: 'ant-col ant-col-third',
            props: {
              options: currencyOptions,
              allowClear: true,
              label: 'Target objective currency',
              required: false,
            },
          },
        ],
      },
      {
        fieldGroupClassName: 'ant-row layout-padding',
        wrappers: ['accordion'],
        props: {
          label: 'Addresses',
        },
        fieldGroup: [
          {
            key: 'addresses',
            type: 'select',
            className: 'ant-col ant-col-third',
            defaultValue: [],
            props: {
              label: 'Addresses',
              multiple: true,
              footer: this.formsService.getFooterSelect(
                'addresses',
                AddressesAddComponent,
                () => {
                  return {
                    companyId: this.pageForm.model.id,
                  };
                }
              ),
              tooltip: this.formsService.getTooltip(
                'addresses',
                AddressesAddComponent
              ),
              options: this.storeService.store.addresses.data$.pipe(
                map((addresses) => {
                  return this.addressesService.getAddressesForOptions(
                    addresses
                  );
                })
              ),
            },
          },
        ],
      },
      ...CommentsNotesFormAndAttachedFilesForm,
    ];
  }

  public async onLoaded() {
    if (this.pageForm?.id) {
      const transactionsColumns =
        await this.transactionsService.getColumnsDefsDisplayed$();

      this.dataTabs = [
        {
          title: 'Addresses',
          dataType: 'addresses',
          datas$: this.addressesService.getAddressesForCompany$(
            this.pageForm.id
          ),
        },
        {
          title: 'Contacts',
          dataType: 'contacts',
          datas$: this.storeService.store.contacts.data$.pipe(
            map((contacts) =>
              contacts.filter(
                (contact) => contact.companyId === this.pageForm.id
              )
            )
          ),
        },
        {
          title: 'Buy Specs',
          dataType: 'buySpecs',
          datas$: this.storeService.store.buySpecs.data$.pipe(
            map((buySpecs) =>
              buySpecs.filter(
                (buySpec) => buySpec.buyerCompanyId === this.pageForm.id
              )
            )
          ),
        },
        {
          title: 'Items',
          dataType: 'items',
          datas$: this.storeService.store.items.data$.pipe(
            map((items) =>
              items.filter(
                (item) => item.supplierCompanyId === this.pageForm.id
              )
            )
          ),
        },
        {
          title: 'Samples',
          dataType: 'samples',
          actionsOnSelection: this.samplesService.actionsOnSelections,
          datas$: combineLatest([
            this.storeService.store.items.data$,
            this.storeService.store.samples.data$,
          ]).pipe(
            map(([items, samples]) => {
              const fItems = items.filter(
                (item) => item.supplierCompanyId === this.pageForm.id
              );

              const fItemsId = fItems.map((item) => item.id);

              if (!fItemsId.length) {
                return [];
              }

              return samples.filter((sample) =>
                fItemsId.includes(sample.itemId)
              );
            })
          ),
        },
        {
          title: 'S&P agreements',
          dataType: 'agreements',
          datas$: this.storeService.store.agreements.data$.pipe(
            map((agreements) =>
              agreements.filter(
                (a) =>
                  a.sellerCompanyId === this.pageForm.id ||
                  a.buyerCompanyId === this.pageForm.id ||
                  a.thirdPartyCompanyId === this.pageForm.id
              )
            )
          ),
        },
        {
          title: 'Transactions',
          datas$: this.transactionsService.transactionsData$.pipe(
            map((transactions) =>
              transactions.filter(
                (transaction) =>
                  transaction.buyerCompanyId === this.pageForm.id ||
                  transaction.sellerCompanyId === this.pageForm.id
              )
            )
          ),
          columns: transactionsColumns,
        },
        {
          title: 'Invoices',
          dataType: 'invoices',
          datas$: this.storeService.store.invoices.data$.pipe(
            map((invoices) =>
              invoices.filter(
                (invoice) => invoice.companyId === this.pageForm.id
              )
            )
          ),
        },
      ];
    }
  }

  public upsertCompany = (model: any) => {
    let companyId = model.id;

    const modelWithoutAddresses = omit(model, ['addresses']);

    return combineLatest([
      companyId
        ? this.storeService.updateObjectByPk<Companies>(
            'companies',
            companyId,
            modelWithoutAddresses
          )
        : this.storeService.addObject<Companies>(
            'companies',
            modelWithoutAddresses
          ),
      companyId
        ? this.storeService.store.companyAddress.data$.pipe(
            take(1),
            map((cc) => cc.filter((c) => c.companyId === companyId))
          )
        : of([]),
    ]).pipe(
      switchMap(([res, oldAddressesId]) => {
        if (!companyId) {
          // It's a new company
          companyId = (res as any as Result<Companies>).returning?.[0].id;
        }

        const newAddressesId: number[] = model.addresses;

        const operationsArray = getDiffArrayOperation<number>(
          oldAddressesId.map((c) => c.addressId),
          newAddressesId
        );

        const newAddresses: CompanyAddress_Insert_Input[] =
          operationsArray.toAdd.map((addressId) => {
            return {
              createdBy: this.userService.userId$.value,
              addressId,
              companyId,
            };
          });

        return combineLatest([
          this.datasService.insertDatas('companyAddress', newAddresses),
          ...operationsArray.toRemove.map((e) => {
            const ca = oldAddressesId.find((c) => c.addressId === e);
            if (ca) {
              return this.datasService.deleteData$('companyAddress', ca.id);
            } else {
              return of(null);
            }
          }),
        ]).pipe(
          tap(() => {
            if (
              operationsArray?.toAdd?.length ||
              operationsArray?.toRemove?.length
            ) {
              this.storeService.update$.next('companyAddress');
            }
          }),
          map(() => res)
        );
      })
    );
  };
}
